import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Switch from 'react-switch';
import moment from 'moment';
import { sum } from 'ramda';
import PropTypes from 'prop-types';
import querystring from 'query-string';
import { MICE_GROUP_PRODUCT } from 'constants/booking';
import { gql } from '@apollo/client';
import { types } from 'types/passengers';

import { clientShape, historyShape, searchParamsShape, stationsShape, userShape } from 'constants/propTypes';
import { ActionButton } from 'components/ui/Button';
import CurrencySelect from 'components/shared/CurrencySelect';
import LanguageSelect from 'components/shared/LanguageSelect';
import { defaultClientData } from 'store/client/state';

import { isInfantsOnLapEnabled } from 'utils/featureFlags';
import { isUserEurostarAgent, isUserInEBSCOffice, getSeniorsNumber } from 'utils/booking';
import { isEurostarOrganization, isUserInCSCOffice, isUserCallCenterAgent } from 'utils/user';

import { getFareTypeValues } from './getFareTypeValues';
import PassengerSelection from './PassengerSelection';
import JourneySelection from './JourneySelection';
import FareType from './FareType';
import { ErrorMessageContainer } from './PassengerSelection/StandardPassengerSelection/StandardPassengerSelection.styles';

import { noPassengers, searchTypes, disabilityTypes } from './BookingMagnet.constants';
import AlertBox from 'components/ui/AlertBox';
import {
  validateStandardRange,
  validateStandardSubscriptionRange,
  validateGroupRange,
  getRdgStations,
  getDefaultStations,
  getFareTypesToDisplay,
  isBookingValid,
  isChildrenOnly,
  hasMoreThan4ChildrenPerAdultYouthSeniors,
  hasMoreThan9Passengers,
  shouldShowRewardFareTypeWarningFor3OrMorePaxs,
  isSNCBStations,
  isDBStations,
  isNSStations,
  areDBPassengersTooYoung,
  shouldAllowFareTypesSelection
} from './BookingMagnet.utils';

import {
  BookingMagnetContainer,
  HeaderContainer,
  BookingForm,
  SwitchContainer,
  PaxSelectionContainer,
  SelectContainer,
  switchColours,
  Header,
  StyledFlexContainer,
  StyledDelimiter
} from './BookingMagnet.styles';
import { GET_CUSTOMER_DETAILS, GET_PROFILE } from '../../store/queries';
import { AGENT_ROLE_VOYAGER_GROUP } from 'constants/user';
import DiscountCard from './DiscountCard';
import { productFamilyCodes } from 'types/productFamilyCodes';
import Modal from 'components/ui/Modal';
import { ConfirmButton } from 'components/ui/Button';

import DBAgesModal from './DBAgesModal/DBAgesModal';

const emptySearchParams = {
  outboundDate: null,
  inboundDate: null,
  origin: null,
  destination: null,
  returningOrigin: null,
  returningDestination: null,
  adults: 0,
  adults12To15: 0,
  adults16Plus: 0,
  children: 0,
  children4Only: 0,
  children5To11: 0,
  infants: 0,
  youths: 0,
  seniors: 0,
  seatedInfants: 0,
  containSNCBStation: false,
  containDBStation: false,
  containNSStation: false,
  adultsEvac: 0,
  childrenEvac: 0,
  seniors60To64: 0,
  seniors65Plus: 0
};

const getInitialState = (canHaveStandardBookings) => ({
  bookingTypeSelected: canHaveStandardBookings ? searchTypes.STANDARD : searchTypes.GROUP,
  disabilityType: null,
  ...noPassengers,
  outboundDate: null,
  inboundDate: null,
  multicity: false,
  productFamilies: undefined,
  discountCard: { value: null },
  contractCode: undefined,
  outboundDatePickerErrorMessage: '',
  inboundDatePickerErrorMessage: '',
  passengerRangeError: '',
  isMember: false,
  subscription: undefined,
  showMaxPaxsForReward: false,
  containSNCBStation: false,
  containNSStation: false,
  containDBStation: false,
  language: null,
  childAges: [],
  youthAges: []
});

const getEmptyFormState = (currency) => ({
  currency,
  outboundDatePickerErrorMessage: '',
  inboundDatePickerErrorMessage: '',
  ...emptySearchParams
});

const getShowRewardFareTypeWarning = (passengerState, fareTypeValue, infantsOnLapEnabled) =>
  shouldShowRewardFareTypeWarningFor3OrMorePaxs({
    adults: passengerState.adults,
    adults12To15: passengerState.adults12To15,
    adults16Plus: passengerState.adults16Plus,
    children: passengerState.children,
    children4Only: passengerState.children4Only,
    children5To11: passengerState.children5To11,
    infants: passengerState.infants,
    seatedInfants: passengerState.seatedInfants,
    youths: passengerState.youths,
    seniors: passengerState.seniors,
    fareTypeValue,
    infantsOnLapEnabled
  });

const BookingMagnet = ({ history, searchParams, stations, client, intl: { formatMessage }, user }) => {
  const [state, setState] = useState(() => getInitialState(user.canHaveStandardBookings));
  const [showYouthsOrChildrenDBAgesModal, setShowYouthsOrChildrenDBAgesModal] = useState(false);
  const hasInitialized = useRef(false);

  const {
    canHaveGroupBookings,
    canHaveStandardBookings,
    canHaveAccessibleTravelBookings,
    discountCards: availableDiscountCards = [],
    paymentCurrencies,
    contracts = []
  } = user;

  const {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    infants,
    seatedInfants,
    youths,
    seniors,
    origin,
    destination,
    outboundDate,
    inboundDate,
    multicity,
    currency,
    fareType,
    passengerRangeError,
    bookingTypeSelected,
    disabilityType,
    wheelchairCompanions,
    nonWheelchairCompanions,
    guideDogs,
    isMember,
    subscription,
    showMaxPaxsForReward,
    discountCard,
    containSNCBStation,
    containDBStation,
    containNSStation,
    seniors60To64,
    adultsEvac,
    childrenEvac,
    seniors65Plus,
    language,
    outboundDatePickerErrorMessage,
    inboundDatePickerErrorMessage,
    contractCode,
    productFamilies,
    miceGroupBooking
  } = state;

  const infantsOnLapEnabled = isInfantsOnLapEnabled();
  const containSNCBDBOrNSStation = containSNCBStation || containDBStation || containNSStation;
  const maxInfants = adults + youths + (containSNCBDBOrNSStation ? seniors60To64 + seniors65Plus : seniors);

  const isPackageBooking = history?.location.state?.isPackageBooking;
  const isNewSearchMode = !isEurostarOrganization(user) && contracts.some(({ code }) => code === user.organization?.code);

  const defaultStations = useMemo(() => getDefaultStations(user, stations), [user, stations]);
  const startingOriginCode = useRef(defaultStations.origin);
  const startingDestinationCode = useRef(defaultStations.destination);

  const setPartialState = useCallback((partialStateOrUpdater) => {
    setState((previousState) => {
      const partialState = typeof partialStateOrUpdater === 'function' ? partialStateOrUpdater(previousState) : partialStateOrUpdater;

      if (!partialState) {
        return previousState;
      }

      return {
        ...previousState,
        ...partialState
      };
    });
  }, []);

  const selectFareType = useCallback(
    ({ value, name }) => {
      setPartialState((previousState) => ({
        ...getFareTypeValues(user, value, previousState.bookingTypeSelected, isNewSearchMode),
        fareType: { value, name },
        miceGroupBooking: MICE_GROUP_PRODUCT === name,
        showMaxPaxsForReward: getShowRewardFareTypeWarning(previousState, value, infantsOnLapEnabled),
        ...(isNewSearchMode && { discountCard: { value: null } })
      }));
    },
    [infantsOnLapEnabled, isNewSearchMode, setPartialState, user]
  );

  const setFirstFareType = useCallback(
    (selectedBookingType) => {
      if (isNewSearchMode) {
        if (selectedBookingType === searchTypes.GROUP || selectedBookingType === searchTypes.WITH_DISABILITY) {
          setPartialState({
            productFamilies: undefined,
            contractCode: user.organization?.code
          });
        } else {
          const currentFareType = user.organization?.code;
          const fareTypeToDisplay = user.productFamilies.find((ft) => ft.code === currentFareType) ?? user.productFamilies[0];

          if (fareTypeToDisplay) {
            selectFareType({
              value: fareTypeToDisplay.code,
              name: fareTypeToDisplay.name
            });
          }
        }

        return;
      }

      const [firstFareType] = getFareTypesToDisplay(
        user,
        selectedBookingType,
        isMember,
        subscription != null,
        disabilityType,
        wheelchairCompanions,
        isPackageBooking
      );

      if (firstFareType) {
        selectFareType({
          value: firstFareType.code,
          name: firstFareType.name
        });
      }
    },
    [disabilityType, isMember, isNewSearchMode, isPackageBooking, selectFareType, setPartialState, subscription, user, wheelchairCompanions]
  );

  const updateQueryFields = useCallback(
    (newQueryFields) => {
      if (bookingTypeSelected === searchTypes.WITH_DISABILITY) {
        setFirstFareType(bookingTypeSelected);
      }

      setPartialState((previousState) => {
        const nextState = {
          ...previousState,
          ...newQueryFields,
          passengerRangeError: ''
        };

        const nextContainSNCBDBOrNSStation = nextState.containSNCBStation || nextState.containDBStation || nextState.containNSStation;
        const nextMaxInfants =
          nextState.adults +
          nextState.youths +
          (nextContainSNCBDBOrNSStation ? nextState.seniors60To64 + nextState.seniors65Plus : nextState.seniors);

        return {
          ...nextState,
          seniors: getSeniorsNumber({
            containSNCBDBOrNSStation: nextContainSNCBDBOrNSStation,
            seniors: nextState.seniors,
            seniors60To64: nextState.seniors60To64,
            seniors65Plus: nextState.seniors65Plus
          }),
          infants: Math.min(nextState.infants, nextMaxInfants),
          showMaxPaxsForReward: getShowRewardFareTypeWarning(nextState, nextState.fareType?.value, infantsOnLapEnabled)
        };
      });
    },
    [bookingTypeSelected, infantsOnLapEnabled, setFirstFareType, setPartialState]
  );

  const changeBookingType = useCallback(
    (selectedBookingType, selectedDisabilityType = null) => {
      setFirstFareType(selectedBookingType);

      setPartialState({
        bookingTypeSelected: selectedBookingType,
        disabilityType: selectedDisabilityType,
        ...noPassengers,
        guideDogs:
          selectedBookingType === searchTypes.WITH_DISABILITY && selectedDisabilityType === disabilityTypes.ASSISTANCE_DOG_ONLY ? 1 : 0,
        passengerRangeError: ''
      });
    },
    [setFirstFareType, setPartialState]
  );

  const setDatePickerErrorMessage = useCallback(
    (id, message) => {
      setPartialState({ [id]: message });
    },
    [setPartialState]
  );

  const clearForm = useCallback(
    (e) => {
      const {
        productFamilies: [{ code, name }]
      } = user;

      e.preventDefault();

      setPartialState({
        ...getEmptyFormState(paymentCurrencies[0]),
        currency: paymentCurrencies[0],
        disabilityType: null,
        fareType: { value: code, name }
      });

      // on clear we need to clear cache which we saved in journey search
      client.writeQuery({
        query: gql`
          query {
            searchParams {
              outboundDate
              inboundDate
              origin
              destination
              returningOrigin
              returningDestination
              adults
              adults12To15
              adults16Plus
              children
              children4Only
              children5To11
              infants
              youths
              seniors
              seatedInfants
            }
          }
        `,
        data: {
          searchParams: {
            ...emptySearchParams,
            __typename: 'SearchParams'
          }
        }
      });

      setFirstFareType(bookingTypeSelected);
    },
    [bookingTypeSelected, client, paymentCurrencies, setFirstFareType, setPartialState, user]
  );

  const swapOriginDestination = useCallback(() => {
    setPartialState((previousState) => ({
      origin: previousState.destination,
      destination: previousState.origin
    }));
  }, [setPartialState]);

  const selectCurrency = useCallback(
    (selectedCurrency) => {
      setPartialState({ currency: selectedCurrency });
    },
    [setPartialState]
  );

  const selectDiscountCard = useCallback(
    (selectedDiscountCard) => {
      setPartialState({ discountCard: selectedDiscountCard });
    },
    [setPartialState]
  );

  const selectLanguage = useCallback(
    (selectedLanguage) => {
      setPartialState({ language: selectedLanguage });
    },
    [setPartialState]
  );

  const handleJourneyChange = useCallback(
    (queryFields) => {
      const newState = { ...state, ...queryFields };

      const hadRdgBefore = getRdgStations(stations, state).length > 0;
      const hasRdgAfter = getRdgStations(stations, newState).length > 0;
      const resetProps = hadRdgBefore === hasRdgAfter ? {} : noPassengers;
      const containsSNCBStation = isSNCBStations(stations, newState).length > 0;
      const containsDBStation = isDBStations(stations, newState).length > 0;
      const containsNSStation = isNSStations(stations, newState).length > 0;
      const hadSNCBDBOrNS = state.containSNCBStation || state.containDBStation || state.containNSStation;
      const hasSNCBDBOrNS = containsSNCBStation || containsDBStation || containsNSStation;

      setPartialState({
        ...queryFields,
        ...resetProps,
        containSNCBStation: containsSNCBStation,
        containDBStation: containsDBStation,
        containNSStation: containsNSStation,
        ...(hadSNCBDBOrNS !== hasSNCBDBOrNS && {
          seniors: 0,
          seniors60To64: 0,
          seniors65Plus: 0,
          infants: 0
        }),
        passengerRangeError: ''
      });
    },
    [setPartialState, state, stations]
  );

  const sendSearchQuery = useCallback(() => {
    const { name: originStationName } = stations.find((s) => s.uic === origin);
    const { name: destinationStationName } = stations.find((s) => s.uic === destination);

    const searchQueryObj = {
      origin,
      destination,
      adults,
      adults12To15,
      adults16Plus,
      children,
      children4Only,
      children5To11,
      infants,
      youths,
      seniors,
      adultsWheelchair: state.adultsWheelchair,
      childrenWheelchair: state.childrenWheelchair,
      guideDogs,
      wheelchairCompanions,
      nonWheelchairCompanions,
      outboundDate: moment(outboundDate).format('YYYY-MM-DD'),
      originStationName,
      destinationStationName,
      bookingTypeSelected,
      seniors60To64,
      seniors65Plus,
      adultsEvac,
      childrenEvac
    };

    const returningOriginStationName = stations.find((s) => s.uic === state.returningOrigin);
    const returningDestinationStationName = stations.find((s) => s.uic === state.returningDestination);

    if (returningOriginStationName && returningDestinationStationName) {
      searchQueryObj.returningOriginStationName = returningOriginStationName.name;
      searchQueryObj.returningDestinationStationName = returningDestinationStationName.name;
    }

    if (state.returningOrigin && multicity) {
      searchQueryObj.returningOrigin = state.returningOrigin;
    }

    if (state.returningDestination && multicity) {
      searchQueryObj.returningDestination = state.returningDestination;
    }

    if (inboundDate && inboundDate.isValid()) {
      searchQueryObj.inboundDate = inboundDate.format('YYYY-MM-DD');
    }

    if (contractCode) {
      searchQueryObj.contractCode = contractCode;
    }

    if (productFamilies) {
      searchQueryObj.productFamilies = productFamilies.join(';');
    }

    if (discountCard.value) {
      searchQueryObj.discountCard = discountCard.value;
    }

    if (language) {
      searchQueryObj.language = language;
    }

    if (isSNCBStations(stations, state).length > 0) {
      searchQueryObj.containSNCBStation = true;
    }

    if (isNSStations(stations, state).length > 0) {
      searchQueryObj.containNSStation = true;
    }

    if (isDBStations(stations, state).length > 0) {
      searchQueryObj.containDBStation = true;

      if (state.childAges.length) {
        const getAges = state.childAges.map(({ value }) => value).join(', ');
        searchQueryObj.childAges = getAges;
      }

      if (state.youthAges.length) {
        const getAges = state.youthAges.map(({ value }) => value).join(', ');
        searchQueryObj.youthAges = getAges;
      }
    }

    if (isNewSearchMode && bookingTypeSelected === searchTypes.GROUP) {
      searchQueryObj.maxTransfers = 0;
    }

    if (isNewSearchMode && fareType?.value !== user.organization?.code) {
      searchQueryObj.productFamilies = productFamilyCodes.CORPORATE;
    }

    searchQueryObj.currency = currency;

    const currentQuery = querystring.parse(history.location.search);
    const searchQuery = querystring.stringify({
      ...currentQuery,
      ...searchQueryObj
    });
    const searchQueryParams = miceGroupBooking ? `${searchQuery}&miceGroupBooking=true` : searchQuery;

    history.push(`/journeys?${searchQueryParams}`, history.location.state);
  }, [
    adults,
    adults12To15,
    adults16Plus,
    adultsEvac,
    bookingTypeSelected,
    children,
    children4Only,
    children5To11,
    childrenEvac,
    contractCode,
    currency,
    destination,
    discountCard,
    fareType?.value,
    guideDogs,
    history,
    inboundDate,
    infants,
    isNewSearchMode,
    language,
    miceGroupBooking,
    multicity,
    nonWheelchairCompanions,
    origin,
    outboundDate,
    productFamilies,
    seniors,
    seniors60To64,
    seniors65Plus,
    state.childAges,
    state.youthAges,
    stations,
    user.organization?.code,
    wheelchairCompanions,
    youths
  ]);

  const submitSearch = useCallback(
    (e) => {
      e.preventDefault();

      const containsRdgStation = getRdgStations(stations, state).length > 0;
      const isStandardBooking = bookingTypeSelected === searchTypes.STANDARD;
      const isGroupBooking = bookingTypeSelected === searchTypes.GROUP;

      let bookingRangeError;

      if (subscription) {
        bookingRangeError = validateStandardSubscriptionRange({
          adults,
          children,
          youths,
          seniors
        });
      }

      if (isStandardBooking && containsRdgStation) {
        bookingRangeError = validateStandardRange({
          adults12To15,
          adults16Plus,
          children4Only,
          children5To11
        });
      }

      if (isGroupBooking) {
        bookingRangeError = validateGroupRange({
          adults,
          children,
          infants,
          seatedInfants,
          youths,
          seniors
        });
      }

      if (bookingRangeError) {
        setPartialState({ passengerRangeError: bookingRangeError });
      } else {
        const shouldTriggerYouthOrChildAgeModal = !isGroupBooking && state.containDBStation && (state.children > 0 || state.youths > 0);
        if (shouldTriggerYouthOrChildAgeModal) {
          return setShowYouthsOrChildrenDBAgesModal(true);
        }

        sendSearchQuery();
        setPartialState({
          ...noPassengers
        });
      }
    },
    [
      adults,
      adults12To15,
      adults16Plus,
      bookingTypeSelected,
      children,
      children4Only,
      children5To11,
      infants,
      seatedInfants,
      sendSearchQuery,
      seniors,
      setPartialState,
      state,
      stations,
      subscription,
      youths
    ]
  );

  const toggleMulticity = useCallback(() => {
    setPartialState((previousState) => ({
      multicity: !previousState.multicity,
      returningOrigin: previousState.destination,
      returningDestination: previousState.origin
    }));
  }, [setPartialState]);

  useEffect(() => {
    if (hasInitialized.current) {
      return undefined;
    }

    hasInitialized.current = true;
    let isActive = true;

    const hydrateSearchParams = async () => {
      if (!searchParams) {
        return;
      }

      const returningDestination = searchParams.returningDestination || (multicity ? startingOriginCode.current : null);
      const returningOrigin = searchParams.returningOrigin || (multicity ? startingDestinationCode.current : null);
      const nextOutboundDate = searchParams.outboundDate && moment(searchParams.outboundDate);
      const nextInboundDate = searchParams.inboundDate && moment(searchParams.inboundDate);
      const nextDestination = searchParams.destination || startingDestinationCode.current;
      const nextOrigin = searchParams.origin || startingOriginCode.current;

      updateQueryFields({
        ...searchParams,
        inboundDate: nextInboundDate,
        outboundDate: nextOutboundDate,
        origin: nextOrigin,
        destination: nextDestination,
        returningOrigin,
        returningDestination,
        containSNCBStation: isSNCBStations(stations, state).length > 0,
        containDBStation: isDBStations(stations, state).length > 0,
        containNSStation: isNSStations(stations, state).length > 0
      });

      setFirstFareType(bookingTypeSelected);
      setPartialState({
        currency: paymentCurrencies[0]
      });

      if (searchParams.returningOrigin) {
        setPartialState({ multicity: true });
      }

      client.writeQuery({
        query: gql`
          query {
            billingDetails
          }
        `,
        data: { billingDetails: defaultClientData.billingDetails }
      });

      const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
      const subscriptionID = new URLSearchParams(history.location.search).get('subscriptionID');

      if (!membershipNumber) {
        return;
      }

      try {
        const result = await client.query({
          query: GET_CUSTOMER_DETAILS,
          variables: {
            membershipNumber
          }
        });

        if (!isActive) {
          return;
        }

        const customerInfo = result?.data?.getCustomerDetails;

        if (customerInfo) {
          setPartialState({
            isMember: true
          });

          if (subscriptionID) {
            try {
              const profileResult = await client.query({
                query: GET_PROFILE,
                variables: {
                  customerId: customerInfo?.profile?.customerId,
                  subscriptionSearchType: 'CURRENT'
                }
              });

              if (!isActive) {
                return;
              }

              const selectedSubscription = profileResult?.data?.profile.subscriptions.find((s) => s.travelPassReference === subscriptionID);

              if (selectedSubscription) {
                setPartialState({
                  subscription: selectedSubscription
                });
              }
            } catch (err) {
              if (isActive) {
                setPartialState({
                  subscription: undefined
                });
              }
            }
          }
        }
      } catch (err) {
        if (isActive) {
          setPartialState({
            isMember: false
          });
        }
      }
    };

    hydrateSearchParams();

    return () => {
      isActive = false;
    };
  }, [
    bookingTypeSelected,
    client,
    history.location.search,
    multicity,
    paymentCurrencies,
    searchParams,
    setFirstFareType,
    setPartialState,
    state,
    stations,
    updateQueryFields
  ]);

  const passengers = {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    infants,
    seatedInfants,
    youths,
    seniors,
    seniors60To64,
    seniors65Plus,
    adultsEvac,
    childrenEvac
  };

  const isGroupBooking = bookingTypeSelected === searchTypes.GROUP;
  const hasChangedPassengerFields =
    sum(
      Object.values({
        ...passengers,
        wheelchairCompanions,
        nonWheelchairCompanions,
        guideDogs
      })
    ) > 0;

  const anyFieldNotEmpty = Boolean(
    (origin && origin.length > 0) ||
      (destination && destination.length > 0) ||
      (outboundDate && outboundDate.isValid()) ||
      (inboundDate && inboundDate.isValid()) ||
      hasChangedPassengerFields ||
      Boolean(disabilityType)
  );

  const fareTypesToDisplay = getFareTypesToDisplay(
    user,
    bookingTypeSelected,
    isMember,
    subscription != null,
    disabilityType,
    wheelchairCompanions,
    isPackageBooking
  );

  const containsRdgStation = getRdgStations(stations, state).length > 0;
  const shouldShowDogOnlyAssistance =
    isUserInEBSCOffice(user) || isUserInCSCOffice(user) || user.canEnableVouchers || user.canDisableVouchers || user.canUpdateVouchers;
  const shouldShowDiscountCard =
    isNewSearchMode &&
    fareType?.value === user.organization?.code &&
    availableDiscountCards?.length > 0 &&
    !(bookingTypeSelected === searchTypes.GROUP || bookingTypeSelected === searchTypes.WITH_DISABILITY);

  const canCreateGroupBookings = isNewSearchMode
    ? (user?.agentRoles ?? []).some((agentRole) => agentRole.includes(AGENT_ROLE_VOYAGER_GROUP))
    : canHaveGroupBookings && !subscription && !isPackageBooking;

  const canCreateAccessibleTravelBookings = canHaveAccessibleTravelBookings && !subscription;
  const dBPassengersAreTooYoung =
    showYouthsOrChildrenDBAgesModal &&
    areDBPassengersTooYoung({
      youthAges: state.youthAges,
      childAges: state.childAges,
      nonWheelchairCompanions: state.nonWheelchairCompanions,
      passengers
    });

  return (
    <BookingMagnetContainer>
      <Modal
        isOpen={showYouthsOrChildrenDBAgesModal}
        title={<FormattedMessage id="bookingPath.addYouthsChildAgesTitle" />}
        subtitle={
          <>
            <FormattedMessage id="bookingPath.addYouthsChildAgesSubtitle" />
          </>
        }
        onClose={() => {
          setPartialState({ childAges: [] });
          setPartialState({ youthAges: [] });
          setShowYouthsOrChildrenDBAgesModal(false);
        }}
      >
        {(state.children > 0 || state.youths > 0) && (
          <StyledFlexContainer>
            {state.children > 0 && (
              <DBAgesModal paxsByType={state.children} type={types.CHILD} ages={state.childAges} setState={setPartialState} />
            )}
            {state.children > 0 && state.youths > 0 && <StyledDelimiter />}
            {state.youths > 0 && (
              <DBAgesModal paxsByType={state.youths} type={types.YOUTH} ages={state.youthAges} setState={setPartialState} />
            )}
          </StyledFlexContainer>
        )}

        {dBPassengersAreTooYoung && (
          <AlertBox variant={AlertBox.variants.WARNING}>
            <FormattedMessage id="bookingPath.youthAndChildrenOnlyAgeRestriction" />
          </AlertBox>
        )}

        <ConfirmButton data-testid={'sendSearch'} onClick={() => sendSearchQuery()} disabled={dBPassengersAreTooYoung}>
          <FormattedMessage id="bookingMagnet.searchTrains" />
        </ConfirmButton>
      </Modal>

      <HeaderContainer>
        <Header>
          <FormattedMessage id="bookingMagnet.heading" />
          {anyFieldNotEmpty && (
            <div data-id="ClearButtonContainer">
              <ActionButton
                onClick={clearForm}
                label={formatMessage({ id: 'bookingMagnet.clearButton' })}
                id="clear-button"
                data-testid="clear-button"
              />
            </div>
          )}
        </Header>
      </HeaderContainer>

      <SwitchContainer>
        <small className="switch-label">{formatMessage({ id: 'bookingMagnet.sameStations' })}</small>
        <Switch
          checked={multicity}
          onChange={toggleMulticity}
          onColor={switchColours.onColor}
          offColor={switchColours.offColor}
          offHandleColor={switchColours.offHandleColor}
          onHandleColor={switchColours.onHandleColor}
          uncheckedIcon={false}
          checkedIcon={false}
          width={40}
          height={20}
          id="multicity-toggle"
          data-testid="multicity-toggle"
        />
        <small className="switch-label">{formatMessage({ id: 'bookingMagnet.differentStations' })}</small>
      </SwitchContainer>

      <BookingForm
        id="booking-form"
        onSubmit={submitSearch}
        autoComplete="off"
        avoidContentJumping={bookingTypeSelected === searchTypes.WITH_DISABILITY}
      >
        <JourneySelection
          onJourneyChange={handleJourneyChange}
          swapOriginDestination={swapOriginDestination}
          outboundDatePickerErrorMessage={outboundDatePickerErrorMessage}
          inboundDatePickerErrorMessage={inboundDatePickerErrorMessage}
          setDatePickerErrorMessage={setDatePickerErrorMessage}
          stations={stations}
          {...state}
        />
        <PaxSelectionContainer>
          <PassengerSelection
            passengerRangeError={passengerRangeError}
            canHaveGroupBookings={canCreateGroupBookings}
            canHaveStandardBookings={canHaveStandardBookings}
            canHaveAccessibleTravelBookings={canCreateAccessibleTravelBookings}
            bookingTypeSelected={bookingTypeSelected}
            expandPaxTypes={containsRdgStation}
            disabilityType={disabilityType}
            onChangePassengers={updateQueryFields}
            changeBookingType={changeBookingType}
            passengers={passengers}
            isEurostarAgent={isUserEurostarAgent(user)}
            shouldShowDogOnlyAssistance={shouldShowDogOnlyAssistance}
            containSNCBDBOrNSStation={containSNCBDBOrNSStation}
            isCallCenterAgent={isUserCallCenterAgent(user)}
            containDBStation={containDBStation}
            infantsOnLapEnabled={infantsOnLapEnabled && bookingTypeSelected === searchTypes.STANDARD}
            maxInfants={maxInfants}
          />
        </PaxSelectionContainer>
        {hasMoreThan9Passengers(state) && (
          <ErrorMessageContainer>
            <FormattedMessage id="bookingMagnet.maxPaxs" />
          </ErrorMessageContainer>
        )}
        {!hasMoreThan9Passengers(state) && isChildrenOnly(state) && (
          <ErrorMessageContainer>
            <FormattedMessage id="bookingMagnet.childrenOnly" />
          </ErrorMessageContainer>
        )}
        {!hasMoreThan9Passengers(state) &&
          !isChildrenOnly(state) &&
          (containSNCBStation || containNSStation) &&
          hasMoreThan4ChildrenPerAdultYouthSeniors(state) && (
            <ErrorMessageContainer>
              <FormattedMessage id="bookingMagnet.maxChildrenPerPax" />
            </ErrorMessageContainer>
          )}
        {showMaxPaxsForReward && (
          <ErrorMessageContainer>
            <FormattedMessage id="bookingMagnet.rewardMaxPaxWarning" />
          </ErrorMessageContainer>
        )}
        <SelectContainer>
          {shouldAllowFareTypesSelection({
            bookingTypeSelected,
            disabilityType,
            isNewSearchModeEnabled: isNewSearchMode,
            containDBStation
          }) && (
            <div>
              <FareType
                label={formatMessage({ id: 'bookingMagnet.fareType' })}
                onSelect={selectFareType}
                fareTypeList={fareTypesToDisplay}
                value={fareType && fareType.value}
                isPackageBooking={isPackageBooking}
              />
            </div>
          )}
          {shouldShowDiscountCard && (
            <div>
              <DiscountCard
                label={formatMessage({ id: 'bookingMagnet.discountCard' })}
                onSelect={selectDiscountCard}
                discountCards={availableDiscountCards}
                value={discountCard && discountCard.value}
              />
            </div>
          )}
          {containDBStation &&
          bookingTypeSelected === searchTypes.WITH_DISABILITY &&
          disabilityType === disabilityTypes.WHEELCHAIR ? null : (
            <div>
              <CurrencySelect
                label={formatMessage({ id: 'bookingMagnet.currency' })}
                onSelect={selectCurrency}
                value={currency}
                isPackageBooking={isPackageBooking}
                isGroupBooking={isGroupBooking}
              />
            </div>
          )}
          {containSNCBStation && (
            <div>
              <LanguageSelect label={formatMessage({ id: 'vouchers.language' })} onSelect={selectLanguage} value={language} />
            </div>
          )}
          {containDBStation &&
          bookingTypeSelected === searchTypes.WITH_DISABILITY &&
          disabilityType === disabilityTypes.WHEELCHAIR ? null : (
            <div>
              <ActionButton
                disabled={!isBookingValid(state, user)}
                label={formatMessage({ id: 'bookingMagnet.searchTrains' })}
                id="submit-button"
                data-testid="submit-button"
                variant="primary"
              />
            </div>
          )}
        </SelectContainer>
      </BookingForm>
    </BookingMagnetContainer>
  );
};

BookingMagnet.propTypes = {
  history: historyShape,
  searchParams: searchParamsShape,
  stations: stationsShape,
  client: clientShape,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  user: userShape
};

export default BookingMagnet;
